"use client"

import {
  PersonListCard,
  Badge,
  Skeleton,
  MatchQualityBadge,
  ListCardTrailingActionsFooter,
  openToWorkTitleBadge,
} from '@yeshuman/sdk'
import type { OpenPersonMatchHandler, UnifiedCandidate } from '@yeshuman/sdk'
import { type ReactNode } from 'react'
import { CandidateCard } from './candidate-card'
import type { OpenJobWorkspacePersonHandler } from '../../lib/job-workspace-person-paths'
import type { ApplicantProfileSummary } from '../../hooks/use-opportunity-applicants'

interface UnifiedCandidateCardProps {
  candidate: UnifiedCandidate
  autoSaveBriefId?: string
  /** Employer job Analysis and other non-recruiter surfaces — no Save to brief. */
  hideSaveButton?: boolean
  renderAction?: (candidate: UnifiedCandidate) => ReactNode
  onOpenPerson?: OpenJobWorkspacePersonHandler | OpenPersonMatchHandler
  profileSummary?: ApplicantProfileSummary | null
}

export function UnifiedCandidateCard({
  candidate,
  autoSaveBriefId,
  hideSaveButton = false,
  renderAction,
  onOpenPerson,
  profileSummary,
}: UnifiedCandidateCardProps) {
  const suppressSave = hideSaveButton || !!renderAction

  if (candidate.full) {
    return (
      <CandidateCard
        candidate={candidate.full}
        autoSaveBriefId={autoSaveBriefId}
        hideSaveButton={suppressSave}
        additionalFooter={renderAction ? renderAction(candidate) : undefined}
        onOpenPerson={onOpenPerson}
        profileSummary={profileSummary}
      />
    )
  }

  const strategies = candidate.strategies ?? (candidate.strategy ? [candidate.strategy] : [])
  const score = candidate.score
  const personModel = {
    id: candidate.profile_id,
    name: candidate.name ?? '',
    avatarUrl: profileSummary?.avatar_url ?? null,
    colorSeed: candidate.profile_id,
  }

  return (
    <PersonListCard
      variant="evaluation"
      person={personModel}
      titleFallback={
        candidate.name ? undefined : <Skeleton className="h-7 w-full max-w-md" />
      }
      titleBadge={openToWorkTitleBadge(profileSummary?.open_to_work)}
      headerAside={
        candidate.rank != null ? (
          <Badge variant="default" className="shrink-0 self-start">
            #{candidate.rank}
          </Badge>
        ) : null
      }
      meta={
        <div className="flex flex-wrap items-center gap-2">
          {score != null ? (
            <MatchQualityBadge score={score} />
          ) : (
            <Skeleton className="h-5 w-16 rounded-md" />
          )}
          {strategies.length > 0 &&
            strategies.map((s) => (
              <Badge key={s} variant="secondarySoft" className="text-xs font-normal">
                {s}
              </Badge>
            ))}
        </div>
      }
      body={
        <div className="flex flex-wrap gap-4 pt-1">
          <Skeleton className="h-4 w-24" />
          <Skeleton className="h-4 w-24" />
          <Skeleton className="h-4 w-16" />
        </div>
      }
      footer={
        renderAction ? (
          <ListCardTrailingActionsFooter>{renderAction(candidate)}</ListCardTrailingActionsFooter>
        ) : suppressSave ? undefined : (
          <ListCardTrailingActionsFooter>
            <Skeleton className="h-8 w-[7.5rem] rounded-md" />
          </ListCardTrailingActionsFooter>
        )
      }
      footerClassName={
        renderAction || !suppressSave ? 'pointer-events-auto px-0' : undefined
      }
    />
  )
}
